import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { findIcon } from "../../views/dashboard/icons";
import { DateRangePicker } from "@ui";
import { useGetProjectDetails } from "../../queries/query-hooks/workspace/workspace-hook";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import { formatLocalizedDate } from "../../common/utils/utils";
import Skeleton from "../skeleton";
import { useGetBrandingDownloadReport } from "../../queries/query-hooks/workspace/brand-ads-hook";
import useBackdrop from "../custom-hooks/use-backdrop";
import toast from "react-hot-toast";
import { useGetSponsoredContentDownloadReport } from "queries/query-hooks/workspace/sponsored-content-hook";

const REPORT_DOWNLOAD_DATE_RANGE = 6; // 6 Months

const DownloadReport = ({
  heading = "",
  reportType = "",
  show = false,
  handleClose = () => {},
}) => {
  const { project_id } = useParams();
  const { data: projectDetails, isLoading: isProjectDetailsLoading } =
    useGetProjectDetails(project_id);

  const { mutate: downloadBrandingReport, isLoading: isBrandingReportLoading } =
    useGetBrandingDownloadReport();
  const {
    mutate: downloadSponsoredReport,
    isLoading: isSponsoredReportLoading,
  } = useGetSponsoredContentDownloadReport();

  const { showBackdrop, hideBackdrop } = useBackdrop();
  const [downloadCompleted, setDownloadCompleted] = useState(false);

  const [dataType, setDataType] = useState("all");
  const [fileType, setFileType] = useState("csv"); // xlsx, csv
  const [searchContent, setSearchContent] = useState("");

  const [dateRangePayload, setDateRangePayload] = useState({
    start_date: "",
    end_date: "",
  });
  const [selectedIds, setSelectedIds] = useState([]);

  const handleSelectedEntity = (item) => {
    setSelectedIds((prev) => {
      const exists = prev.some((el) => el.id === item.id);
      if (exists) {
        return prev.filter((el) => el.id !== item.id);
      } else {
        return [...prev, item];
      }
    });
  };

  useEffect(() => {
    if (projectDetails && projectDetails?.length > 0) {
      const today = dayjs();
      const projectEndDate = dayjs(projectDetails[0]?.project_end_date);
      let result = projectDetails[0]?.project_end_date;

      if (
        projectEndDate &&
        (projectEndDate.isAfter(today) || projectEndDate.isSame(today, "day"))
      ) {
        result = today.subtract(1, "day");
      } else {
        result = projectEndDate;
      }

      setDateRangePayload({
        start_date: formatLocalizedDate(
          new Date(projectDetails[0]?.project_start_date),
          "YYYY-MM-DD[T]00:00:00.000Z"
        ),
        end_date: formatLocalizedDate(
          new Date(result),
          "YYYY-MM-DD[T]23:59:59.000Z"
        ),
      });
    }
  }, [projectDetails]);

  const projectStartDate =
    projectDetails?.length > 0 ? projectDetails[0]?.project_start_date : "";

  const isFutureDate = dayjs(projectStartDate).isAfter(dayjs());

  const projectEndDate =
    projectDetails?.length > 0 ? projectDetails[0]?.project_end_date : "";

  const today = new Date();
  let endDateLimit = new Date(projectEndDate);

  const todayDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const endDateOnly = new Date(
    endDateLimit.getFullYear(),
    endDateLimit.getMonth(),
    endDateLimit.getDate()
  );

  if (endDateOnly.getTime() >= todayDate.getTime()) {
    endDateLimit = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() - 1
    );
  }

  const handleSelectAll = () => {
    const allEntities =
      reportType === "Campaigns"
        ? projectDetails?.at(0)?.project_group_data?.map((item) => ({
            id: item?.group_data?.id,
            name: item?.group_data?.group_title,
          }))
        : projectDetails?.at(0)?.project_contents?.flatMap((item) =>
            item?.fetch_with_contents?.map((content) => ({
              id: content?.content_id,
              name: content?.content_title,
            }))
          );

    setSelectedIds(allEntities || []);
  };
  const handleDeselectAll = () => {
    setSelectedIds([]);
  };
  const handleRemoveEntity = (item) => {
    setSelectedIds((prev) => prev.filter((i) => i.id !== item.id));
  };

  const downloadFileFromUrl = (url, fileName) => {
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName || "file");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadReport = () => {
    if (isFutureDate) {
      toast.error("Not available for upcoming projects");
      return;
    }
    if (!!REPORT_DOWNLOAD_DATE_RANGE) {
      const startDatejs = dayjs(dateRangePayload.start_date);
      const endDatejs = dayjs(dateRangePayload.end_date);
      const isInBetweenDateRange =
        endDatejs.diff(startDatejs, "month", true) <=
        REPORT_DOWNLOAD_DATE_RANGE;

      if (!isInBetweenDateRange) {
        toast.error(
          `Report downloads are limited to ${REPORT_DOWNLOAD_DATE_RANGE} month period`
        );
        return;
      }
    }
    showBackdrop();
    let payload = {
      start_date: dateRangePayload.start_date,
      end_date: dateRangePayload.end_date,
      file_type: fileType,
      data_type: dataType,
    };
    if (reportType === "Campaigns") {
      payload = {
        ...payload,
        campaign_id: selectedIds.map((item) => item.id),
      };
      if (dataType === "all") delete payload.campaign_id;
    } else {
      payload = {
        ...payload,
        content_id: selectedIds.map((item) => item.id)?.toString(),
      };
      if (dataType === "all") delete payload.content_id;
    }

    function onSuccessReportDownload(response) {
      if (!response?.data?.data || response?.data?.data?.length === 0) {
        toast.error("No data for the selected date range.");
        hideBackdrop();
        closeModalHandler();
        return;
      }
      const files = response?.data?.data;
      if (!files || typeof files !== "object") {
        console.error("Invalid response structure", files);
        return;
      }
      const zipFile = files?.zip_file;
      if (!zipFile) {
        console.error("No zip file found in response", files);
        return;
      }

      const fileName = zipFile.split("/").pop();
      downloadFileFromUrl(zipFile, fileName);

      hideBackdrop();
      setDownloadCompleted(true);

      setTimeout(() => {
        closeModalHandler();
      }, 2000);
    }
    function onErrorReportDownload(error) {
      console.error("Download failed", error);
      hideBackdrop();
      closeModalHandler();
    }

    if (reportType === "Campaigns") {
      downloadBrandingReport(
        {
          project_id,
          payload,
        },
        {
          onSuccess: (res) => onSuccessReportDownload(res),
          onError: (error) => onErrorReportDownload(error),
        }
      );
    } else {
      downloadSponsoredReport(
        {
          project_id,
          payload,
        },
        {
          onSuccess: (res) => onSuccessReportDownload(res),
          onError: (error) => onErrorReportDownload(error),
        }
      );
    }
  };

  const closeModalHandler = () => {
    handleClose();
    setDataType("all");
    setFileType("csv");
    setSearchContent("");
    setSelectedIds([]);
    setDownloadCompleted(false);
  };
  const listOfEntities =
    reportType === "Campaigns"
      ? projectDetails?.at(0)?.project_group_data.map((item) => ({
          id: item?.group_data?.id,
          name: item?.group_data?.group_title,
        }))
      : projectDetails?.at(0)?.project_contents?.flatMap((item) =>
          item?.fetch_with_contents?.map((content) => ({
            id: content?.content_id,
            name: content?.content_title,
          }))
        );

  return (
    <Modal show={show} onHide={closeModalHandler} centered>
      <Modal.Header closeButton={closeModalHandler}>
        <Modal.Title>
          <h6 className="m-0 fw-semibold">{`${
            !downloadCompleted ? heading : "Success"
          }`}</h6>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="mb-2">
        {!downloadCompleted ? (
          <div className="d-flex flex-column gap-4">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <div
                  className="btn-group"
                  role="group"
                  aria-label="Basic example"
                >
                  <button
                    type="button"
                    className={`btn ${
                      dataType === "all" ? "btn-primary" : "btn-light"
                    }`}
                    onClick={() => setDataType("all")}
                  >
                    All {reportType}
                  </button>
                  <button
                    type="button"
                    className={`btn ${
                      dataType === "selected" ? "btn-primary" : "btn-light"
                    }`}
                    onClick={() => setDataType("selected")}
                  >
                    Select {reportType}
                  </button>
                </div>
              </div>

              {/* <div>
                <select
                  className="form-select py-2 text-black"
                  value={fileType}
                  onChange={(e) => {
                    setFileType(e.target.value);
                  }}
                >
                  <option value="xlsx" className="text-black">
                    Excel
                  </option>
                  <option value="csv" className="text-black">
                    CSV
                  </option>
                </select>
              </div> */}
            </div>

            <div className="d-flex flex-column gap-2">
              <p className="m-0 text-black poppins-medium">
                Date Range<span className="text-danger">*</span>
              </p>
              {isProjectDetailsLoading ? (
                <Skeleton variant="text" height={50} width={"100%"} />
              ) : (
                <DateRangePicker
                  startDate={projectStartDate}
                  projectEndDate={projectEndDate}
                  endDate={endDateLimit}
                  setDateRangePayload={setDateRangePayload}
                  dateLabel={dateRangePayload}
                  rangeLimit={REPORT_DOWNLOAD_DATE_RANGE}
                />
              )}
            </div>

            {dataType === "selected" ? (
              <div className="d-flex flex-column gap-2">
                <div className="d-flex justify-content-between align-items-center">
                  <p className="m-0 text-black poppins-medium">
                    Select {reportType}
                    <span className="text-danger">*</span>
                  </p>
                  <p className="m-0">
                    <span>{selectedIds?.length}</span> selected
                  </p>
                </div>
                <div className="mb-2">
                  <div className="position-relative w-100">
                    <div
                      className="position-absolute top-50 start-0 translate-middle-y ms-3"
                      style={{ pointerEvents: "none" }}
                    >
                      {findIcon("Search", "outline", "24")}
                    </div>
                    <input
                      className="form-control ps-6"
                      type="search"
                      placeholder={`Search ${reportType}`}
                      aria-label="Search"
                      value={searchContent}
                      onChange={(e) => {
                        setSearchContent(e.target.value);
                      }}
                    />
                  </div>
                </div>
                <div className="d-flex justify-content-between">
                  <p
                    className="m-0 text-primary cursor-pointer"
                    onClick={handleSelectAll}
                  >
                    Select all
                  </p>
                  <p
                    className={`m-0 cursor-pointer ${
                      selectedIds?.length === 0 ? "text-muted" : "text-danger"
                    }`}
                    onClick={handleDeselectAll}
                  >
                    Clear all
                  </p>
                </div>
                <div className="d-flex flex-column gap-3">
                  <div className="list-group border border-1">
                    {listOfEntities
                      ?.filter(
                        (item) =>
                          item?.name
                            ?.toLowerCase()
                            ?.includes(searchContent?.toLowerCase()) ||
                          item?.id?.toString()?.includes(searchContent)
                      )
                      ?.map((item, index) => (
                        <div
                          key={item.id}
                          className="list-group-item d-flex justify-content-between align-items-center border-0"
                        >
                          <div className="form-check d-flex align-items-center">
                            <input
                              className="form-check-input me-2 mb-1 col-md-2"
                              type="checkbox"
                              id={`checkbox-${index}`}
                              checked={selectedIds.some(
                                (selected) => selected.id === item.id
                              )}
                              onChange={() => handleSelectedEntity(item)}
                            />
                            <span className="form-check-label">
                              {item.name}
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>

                  {selectedIds?.length > 0 ? (
                    <>
                      <div className="d-flex flex-column gap-2">
                        <p className="m-0 text-black">Selected {reportType}</p>
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        {selectedIds?.map((item) => {
                          return (
                            <div
                              key={item.id}
                              className="d-flex align-items-center gap-2 text-primary bg-soft-primary rounded-pill px-3 py-2 fs-7"
                              title={item.name}
                            >
                              <p
                                style={{ maxWidth: "160px" }}
                                className="m-0 text-truncate"
                              >
                                {item.name}
                              </p>
                              <span
                                className="m-0 cursor-pointer"
                                onClick={() => handleRemoveEntity(item)}
                              >
                                {findIcon("Close-2", "outline", "18")}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="d-flex flex-column justify-content-center align-items-center text-center gap-3 p-3">
            <div>{findIcon("correct download", "solid", "24")}</div>
            <div>
              <p className="text-black">
                The report you requested has not been downloaded successfully
              </p>
            </div>
          </div>
        )}
      </Modal.Body>
      {!downloadCompleted ? (
        <Modal.Footer>
          <Button variant="light" onClick={closeModalHandler}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={handleDownloadReport}
            className="px-3 py-2 d-flex align-items-center gap-2"
            disabled={dataType === "selected" && selectedIds?.length === 0}
          >
            {findIcon("DownloadReport", "dual-tone", "18")}
            <span>Download Report</span>
          </Button>
        </Modal.Footer>
      ) : null}
    </Modal>
  );
};

export default DownloadReport;
